import { useState, useEffect, useMemo } from 'react';
import '../App.css';
import { useApi } from '../hooks/useApi';
import { useAuth } from '../hooks/useAuth';
import logger from "../utils/logger";
import { fmtDate } from '../utils/ruDates';
import { matchesSearch } from '../utils/search';
function OzonAnalytics({
  accountId,
  accountName,
  openLightbox,
  projectMode = false,
  projectCabinets = []
}) {
  const {
    authFetch,
    API_BASE_URL
  } = useApi();
  const {
    isAuthenticated
  } = useAuth();

  // Get list of account IDs to fetch data for
  const accountIds = projectMode && projectCabinets.length > 0 ? projectCabinets.map(c => c.id) : accountId ? [accountId] : [];

  // Create mapping of accountId -> accountName for project mode
  const accountNameMap = useMemo(() => {
    const map = {};
    if (projectMode && projectCabinets.length > 0) {
      for (const cab of projectCabinets) {
        map[cab.id] = cab.account_name || cab.name || cab.id;
      }
    } else if (accountId) {
      map[accountId] = accountName;
    }
    return map;
  }, [projectMode, projectCabinets, accountId, accountName]);
  // Periods
  const [period1From, setPeriod1From] = useState('');
  const [period1To, setPeriod1To] = useState('');
  const [period2From, setPeriod2From] = useState('');
  const [period2To, setPeriod2To] = useState('');

  // Grouping
  const [groupBy, setGroupBy] = useState('sku'); // 'sku' | 'model'

  // Data
  const [rawComparison, setRawComparison] = useState(null);
  const [productData, setProductData] = useState({}); // SKU -> {offer_id, image}
  const [loading, setLoading] = useState(false);

  // Filters (client-side)
  const [skuFilter, setSkuFilter] = useState([]); // Selected SKUs for filtering
  const [hideZeros, setHideZeros] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [showShopColumn, setShowShopColumn] = useState(false);

  // Filter UI
  const [productSearch, setProductSearch] = useState('');
  const [productResults, setProductResults] = useState([]);
  const [showPasteModal, setShowPasteModal] = useState(false);
  const [pasteText, setPasteText] = useState('');

  // Sorting
  const [sortField, setSortField] = useState(null);
  const [sortDir, setSortDir] = useState('desc');

  // Period presets
  const applyPreset = preset => {
    const today = new Date();
    let from, to;
    switch (preset) {
      case 'week':
        to = new Date(today);
        to.setDate(to.getDate() - 1); // Yesterday
        from = new Date(to);
        from.setDate(from.getDate() - 6); // 7 days
        break;
      case 'month':
        to = new Date(today);
        to.setDate(to.getDate() - 1);
        from = new Date(to);
        from.setDate(from.getDate() - 29); // 30 days
        break;
      case 'quarter':
        to = new Date(today);
        to.setDate(to.getDate() - 1);
        from = new Date(to);
        from.setDate(from.getDate() - 89); // 90 days
        break;
      default:
        return;
    }
    const formatDate = d => d.toISOString().split('T')[0];

    // Period 2 = recent (newer), Period 1 = same duration immediately before
    const duration = Math.floor((to - from) / (1000 * 60 * 60 * 24));
    const p1ToDate = new Date(from);
    p1ToDate.setDate(p1ToDate.getDate() - 1);
    const p1FromDate = new Date(p1ToDate);
    p1FromDate.setDate(p1FromDate.getDate() - duration);
    setPeriod1From(formatDate(p1FromDate));
    setPeriod1To(formatDate(p1ToDate));
    setPeriod2From(formatDate(from));
    setPeriod2To(formatDate(to));
  };

  // Auto-calculate period 2 when period 1 changes
  const handlePeriod1Change = (from, to) => {
    setPeriod1From(from);
    setPeriod1To(to);
    if (from && to) {
      const fromDate = new Date(from);
      const toDate = new Date(to);
      const duration = Math.floor((toDate - fromDate) / (1000 * 60 * 60 * 24));
      const period2ToDate = new Date(fromDate);
      period2ToDate.setDate(period2ToDate.getDate() - 1);
      const period2FromDate = new Date(period2ToDate);
      period2FromDate.setDate(period2FromDate.getDate() - duration);
      setPeriod2From(period2FromDate.toISOString().split('T')[0]);
      setPeriod2To(period2ToDate.toISOString().split('T')[0]);
    }
  };

  // Swap periods
  const swapPeriods = () => {
    const temp1From = period1From;
    const temp1To = period1To;
    setPeriod1From(period2From);
    setPeriod1To(period2To);
    setPeriod2From(temp1From);
    setPeriod2To(temp1To);
  };

  // Aggregate comparison results from multiple accounts
  // accountIdsOrder - array of account IDs in the same order as results
  const aggregateComparisons = (results, accountIdsOrder) => {
    if (results.length === 0) return null;
    if (results.length === 1) {
      // Add sourceAccountId to each SKU from single result
      const singleResult = results[0];
      const accId = accountIdsOrder[0];
      for (const item of singleResult.by_sku || []) {
        item.sourceAccountId = accId;
        // Calculate avg_check
        const rev = item.metrics?.revenue;
        const units = item.metrics?.ordered_units;
        if (rev && units) {
          const avgP1 = units.period1 > 0 ? rev.period1 / units.period1 : 0;
          const avgP2 = units.period2 > 0 ? rev.period2 / units.period2 : 0;
          item.metrics.avg_check = {
            period1: avgP1,
            period2: avgP2,
            change: avgP2 - avgP1,
            change_percent: avgP1 > 0 ? (avgP2 - avgP1) / avgP1 * 100 : null
          };
        }
      }
      return singleResult;
    }

    // Aggregate totals (period1/period2 have structure like { from, to, revenue, ordered_units, ... })
    const aggregated = {
      period1: {
        ...results[0].period1
      },
      period2: results[0].period2 ? {
        ...results[0].period2
      } : null,
      by_sku: []
    };

    // Numeric fields in period1/period2 objects
    const periodNumericFields = ['revenue', 'ordered_units', 'hits_view', 'hits_view_pdp', 'hits_view_search', 'hits_tocart', 'hits_tocart_pdp', 'hits_tocart_search', 'session_view', 'session_view_pdp', 'session_view_search'];

    // Sum up numeric fields in period1/period2
    for (let i = 1; i < results.length; i++) {
      const r = results[i];
      for (const field of periodNumericFields) {
        if (r.period1?.[field] !== undefined) {
          aggregated.period1[field] = (aggregated.period1[field] || 0) + r.period1[field];
        }
        if (r.period2?.[field] !== undefined) {
          if (!aggregated.period2) aggregated.period2 = {
            ...r.period2
          };else aggregated.period2[field] = (aggregated.period2[field] || 0) + r.period2[field];
        }
      }
    }

    // Merge by_sku arrays - group by SKU and aggregate metrics
    // by_sku item structure: { sku, name, metrics: { revenue: { period1, period2, change, change_percent }, ... } }
    const metricNames = ['revenue', 'ordered_units'];
    const skuMap = new Map();
    for (let i = 0; i < results.length; i++) {
      const r = results[i];
      const accId = accountIdsOrder[i];
      for (const item of r.by_sku || []) {
        const existing = skuMap.get(item.sku);
        if (existing) {
          // Aggregate metrics
          for (const metricName of metricNames) {
            const itemMetric = item.metrics?.[metricName];
            const existingMetric = existing.metrics?.[metricName];
            if (itemMetric && existingMetric) {
              existingMetric.period1 = (existingMetric.period1 || 0) + (itemMetric.period1 || 0);
              existingMetric.period2 = (existingMetric.period2 || 0) + (itemMetric.period2 || 0);
            }
          }
          // Track multiple source accounts
          if (!existing.sourceAccountIds.includes(accId)) {
            existing.sourceAccountIds.push(accId);
          }
          if (item.sku_count) {
            existing.sku_count = (existing.sku_count || 0) + item.sku_count;
          }
        } else {
          const newItem = JSON.parse(JSON.stringify(item));
          newItem.sourceAccountId = accId;
          newItem.sourceAccountIds = [accId];
          skuMap.set(item.sku, newItem);
        }
      }
    }

    // Recalculate change and change_percent for aggregated SKUs
    for (const item of skuMap.values()) {
      for (const metricName of metricNames) {
        const metric = item.metrics?.[metricName];
        if (metric) {
          const p1 = metric.period1 || 0;
          const p2 = metric.period2 || 0;
          metric.change = p2 - p1;
          metric.change_percent = p1 > 0 ? (p2 - p1) / p1 * 100 : null;
        }
      }
      // Calculate avg_check (revenue / ordered_units)
      const rev = item.metrics?.revenue;
      const units = item.metrics?.ordered_units;
      if (rev && units) {
        const avgP1 = units.period1 > 0 ? rev.period1 / units.period1 : 0;
        const avgP2 = units.period2 > 0 ? rev.period2 / units.period2 : 0;
        item.metrics.avg_check = {
          period1: avgP1,
          period2: avgP2,
          change: avgP2 - avgP1,
          change_percent: avgP1 > 0 ? (avgP2 - avgP1) / avgP1 * 100 : null
        };
      }
    }
    aggregated.by_sku = Array.from(skuMap.values());
    return aggregated;
  };

  // Fetch comparison data
  const fetchComparison = async () => {
    if (!period1From || !period1To) {
      alert('Укажите первый период');
      return;
    }
    if (accountIds.length === 0) {
      alert('Нет кабинетов для загрузки');
      return;
    }
    setLoading(true);
    try {
      const payload = {
        period1_from: new Date(period1From).toISOString(),
        period1_to: new Date(period1To).toISOString(),
        group_by: groupBy,
      };
      if (period2From && period2To) {
        payload.period2_from = new Date(period2From).toISOString();
        payload.period2_to = new Date(period2To).toISOString();
      }

      // Fetch data from all accounts
      const promises = accountIds.map(accId => authFetch(`${API_BASE_URL}/api/v1/ozon-analytics/${accId}/compare`, {
        method: 'POST',
        body: JSON.stringify(payload)
      }).catch(err => {
        logger.error(`Failed to fetch analytics for account ${accId}:`, err);
        return null;
      }));
      const results = await Promise.all(promises);
      const validResults = results.filter(r => r !== null);
      if (validResults.length === 0) {
        throw new Error('Не удалось загрузить данные ни по одному кабинету');
      }

      // Aggregate results if multiple accounts
      const aggregatedResult = aggregateComparisons(validResults, accountIds);
      setRawComparison(aggregatedResult);

      // Fetch product data (images, offer_ids) from assortment.
      // Skipped in model mode: "sku" is a model name there, not a real SKU.
      if (groupBy !== 'model' && aggregatedResult.by_sku && aggregatedResult.by_sku.length > 0) {
        await fetchProductData();
      } else if (groupBy === 'model') {
        setProductData({});
      }
    } catch (error) {
      logger.error('Failed to fetch analytics:', error);
      alert('Ошибка при загрузке аналитики: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  // Fetch product data (images, offer_ids) from sku-mapping endpoint
  const fetchProductData = async () => {
    try {
      // Fetch SKU mappings from all accounts
      const promises = accountIds.map(accId => authFetch(`${API_BASE_URL}/api/v1/ozon/${accId}/sku-mapping`).catch(err => {
        logger.error(`Failed to fetch product data for account ${accId}:`, err);
        return {};
      }));
      const results = await Promise.all(promises);

      // Merge all product data
      const merged = {};
      for (const data of results) {
        Object.assign(merged, data || {});
      }
      setProductData(merged);
    } catch (error) {
      logger.error('Failed to fetch product data:', error);
    }
  };

  // Search products for filter (from assortment) - search across all cabinets in project mode
  const searchProducts = async query => {
    if (!isAuthenticated || !query || query.length < 2 || accountIds.length === 0) {
      setProductResults([]);
      return;
    }
    try {
      // Search across all accounts
      const promises = accountIds.map(accId => authFetch(`${API_BASE_URL}/api/v1/ozon/${accId}/assortment?search=${encodeURIComponent(query)}&limit=10&offset=0`).catch(err => {
        logger.error(`Failed to search products for account ${accId}:`, err);
        return {
          rows: []
        };
      }));
      const results = await Promise.all(promises);

      // Merge results, avoiding duplicates by SKU
      const seenSkus = new Set();
      const mergedResults = [];
      for (const data of results) {
        for (const product of data.rows || []) {
          if (!seenSkus.has(product.sku)) {
            seenSkus.add(product.sku);
            mergedResults.push(product);
          }
        }
      }

      // Limit to 10 results
      setProductResults(mergedResults.slice(0, 10));
    } catch (error) {
      logger.error('Failed to search products:', error);
      setProductResults([]);
    }
  };
  useEffect(() => {
    const timer = setTimeout(() => {
      searchProducts(productSearch);
    }, 300);
    return () => clearTimeout(timer);
  }, [productSearch]);

  // Add product to filter
  const addToFilter = (sku, name) => {
    if (!skuFilter.find(f => f.sku === sku)) {
      setSkuFilter([...skuFilter, {
        sku,
        name
      }]);
    }
    setProductSearch('');
    setProductResults([]);
  };

  // Remove from filter
  const removeFromFilter = sku => {
    setSkuFilter(skuFilter.filter(f => f.sku !== sku));
  };

  // Clear filter
  const clearFilter = () => {
    setSkuFilter([]);
  };

  // Handle paste import
  const handlePasteImport = () => {
    if (!pasteText.trim()) return;
    const lines = pasteText.trim().split(/[\n,;]+/);
    const newSkus = [];
    for (const line of lines) {
      const sku = line.trim();
      if (sku && !skuFilter.find(f => f.sku === sku)) {
        newSkus.push({
          sku,
          name: ''
        });
      }
    }
    if (newSkus.length > 0) {
      setSkuFilter([...skuFilter, ...newSkus]);
    }
    setPasteText('');
    setShowPasteModal(false);
  };

  // Client-side filtering and sorting
  const filteredData = useMemo(() => {
    if (!rawComparison || !rawComparison.by_sku) return [];
    let data = [...rawComparison.by_sku];

    // Filter by selected SKUs
    if (skuFilter.length > 0) {
      const filterSkus = new Set(skuFilter.map(f => f.sku));
      data = data.filter(item => filterSkus.has(item.sku));
    }

    // Filter by search query
    if (searchQuery.trim()) {
      data = data.filter(item => matchesSearch([item, productData[item.sku]], searchQuery));
    }

    // Hide zeros
    if (hideZeros) {
      data = data.filter(item => {
        const revenue1 = item.metrics.revenue?.period1 || 0;
        const revenue2 = item.metrics.revenue?.period2 || 0;
        const units1 = item.metrics.ordered_units?.period1 || 0;
        const units2 = item.metrics.ordered_units?.period2 || 0;
        return revenue1 > 0 || revenue2 > 0 || units1 > 0 || units2 > 0;
      });
    }

    // Sort
    if (sortField) {
      data.sort((a, b) => {
        let valA, valB;
        if (sortField === 'sku') {
          valA = a.sku;
          valB = b.sku;
        } else if (sortField === 'name') {
          valA = a.name;
          valB = b.name;
        } else if (sortField === 'offer_id') {
          valA = productData[a.sku]?.offer_id || '';
          valB = productData[b.sku]?.offer_id || '';
        } else if (sortField === 'sku_count') {
          valA = a.sku_count || 0;
          valB = b.sku_count || 0;
        } else {
          // Metric fields like 'revenue_p1', 'ordered_units_p2', 'avg_check_change', etc.
          // Split by last underscore to handle metrics with underscores (ordered_units, avg_check)
          const lastUnderscoreIdx = sortField.lastIndexOf('_');
          const metric = sortField.substring(0, lastUnderscoreIdx);
          const period = sortField.substring(lastUnderscoreIdx + 1);
          if (period === 'p1') {
            valA = a.metrics[metric]?.period1 || 0;
            valB = b.metrics[metric]?.period1 || 0;
          } else if (period === 'p2') {
            valA = a.metrics[metric]?.period2 || 0;
            valB = b.metrics[metric]?.period2 || 0;
          } else if (period === 'change') {
            valA = a.metrics[metric]?.change || 0;
            valB = b.metrics[metric]?.change || 0;
          } else if (period === 'pct') {
            valA = a.metrics[metric]?.change_percent || 0;
            valB = b.metrics[metric]?.change_percent || 0;
          }
        }
        if (typeof valA === 'string') {
          return sortDir === 'asc' ? valA.localeCompare(valB) : valB.localeCompare(valA);
        }
        return sortDir === 'asc' ? valA - valB : valB - valA;
      });
    }
    return data;
  }, [rawComparison, skuFilter, searchQuery, hideZeros, sortField, sortDir, productData]);

  // Recalculate aggregated stats based on filtered data
  const aggregatedStats = useMemo(() => {
    if (!filteredData || filteredData.length === 0) return null;
    const totals1 = {
      revenue: 0,
      ordered_units: 0
    };
    const totals2 = {
      revenue: 0,
      ordered_units: 0
    };
    for (const item of filteredData) {
      totals1.revenue += item.metrics.revenue?.period1 || 0;
      totals2.revenue += item.metrics.revenue?.period2 || 0;
      totals1.ordered_units += item.metrics.ordered_units?.period1 || 0;
      totals2.ordered_units += item.metrics.ordered_units?.period2 || 0;
    }
    const revenueChange = totals2.revenue - totals1.revenue;
    const revenuePct = totals1.revenue > 0 ? revenueChange / totals1.revenue * 100 : null;
    const unitsChange = totals2.ordered_units - totals1.ordered_units;
    const unitsPct = totals1.ordered_units > 0 ? unitsChange / totals1.ordered_units * 100 : null;
    const avgCheck1 = totals1.ordered_units > 0 ? totals1.revenue / totals1.ordered_units : 0;
    const avgCheck2 = totals2.ordered_units > 0 ? totals2.revenue / totals2.ordered_units : 0;
    const avgCheckChange = avgCheck2 - avgCheck1;
    const avgCheckPct = avgCheck1 > 0 ? avgCheckChange / avgCheck1 * 100 : null;
    return {
      revenue: {
        period1: totals1.revenue,
        period2: totals2.revenue,
        change: revenueChange,
        change_percent: revenuePct
      },
      ordered_units: {
        period1: totals1.ordered_units,
        period2: totals2.ordered_units,
        change: unitsChange,
        change_percent: unitsPct
      },
      average_check: {
        period1: avgCheck1,
        period2: avgCheck2,
        change: avgCheckChange,
        change_percent: avgCheckPct
      }
    };
  }, [filteredData]);

  // Handle sort click
  const handleSort = field => {
    if (sortField === field) {
      setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDir('desc');
    }
  };

  // Format helpers
  const formatNumber = num => {
    if (num === null || num === undefined) return '-';
    return new Intl.NumberFormat('ru-RU', {
      maximumFractionDigits: 2
    }).format(num);
  };
  const formatPercent = num => {
    if (num === null || num === undefined) return '-';
    const sign = num > 0 ? '+' : '';
    return `${sign}${formatNumber(num)}%`;
  };
  const formatDate = dateStr => {
    return fmtDate(dateStr);
  };

  // Change class helper
  const getChangeClass = value => {
    if (value === null || value === undefined || value === 0) return '';
    return value > 0 ? 'change-positive' : 'change-negative';
  };

  // Export CSV (semicolon delimiter for Russian Excel)
  const exportCSV = () => {
    if (!filteredData || filteredData.length === 0) return;
    const headers = ['Магазин', 'Артикул', 'SKU', 'Название', 'Заказано на сумму (П1)', 'Заказано на сумму (П2)', 'Δ Сумма', '% Сумма', 'Заказано товаров (П1)', 'Заказано товаров (П2)', 'Δ Товаров', '% Товаров', 'Средний чек (П1)', 'Средний чек (П2)', 'Δ Чек', '% Чек'];
    const escapeCSV = val => {
      const s = String(val ?? '');
      return s.includes(';') || s.includes('"') || s.includes('\n') ? '"' + s.replace(/"/g, '""') + '"' : s;
    };
    const rows = filteredData.map(item => {
      const pd = productData[item.sku] || {};
      // Get shop name(s) for this SKU
      const shopName = item.sourceAccountIds && item.sourceAccountIds.length > 1 ? item.sourceAccountIds.map(id => accountNameMap[id] || id).join(', ') : accountNameMap[item.sourceAccountId] || accountName || '';
      return [shopName, pd.offer_id || '', item.sku, item.name, item.metrics.revenue?.period1 || 0, item.metrics.revenue?.period2 || 0, item.metrics.revenue?.change || 0, item.metrics.revenue?.change_percent?.toFixed(2) || '', item.metrics.ordered_units?.period1 || 0, item.metrics.ordered_units?.period2 || 0, item.metrics.ordered_units?.change || 0, item.metrics.ordered_units?.change_percent?.toFixed(2) || '', item.metrics.avg_check?.period1?.toFixed(2) || 0, item.metrics.avg_check?.period2?.toFixed(2) || 0, item.metrics.avg_check?.change?.toFixed(2) || 0, item.metrics.avg_check?.change_percent?.toFixed(2) || ''].map(escapeCSV).join(';');
    });
    const csvContent = '﻿' + headers.join(';') + '\n' + rows.join('\n');
    const blob = new Blob([csvContent], {
      type: 'text/csv;charset=utf-8;'
    });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `ozon_analytics_${period1From}_${period1To}.csv`;
    link.click();
  };

  // Sort indicator
  const SortIndicator = ({
    field
  }) => {
    if (sortField !== field) return <span className="sort-indicator">⇅</span>;
    return <span className="sort-indicator active">{sortDir === 'asc' ? '↑' : '↓'}</span>;
  };
  return <div className="ozon-analytics-panel">
            {/* Period Selection */}
            <div className="analytics-controls">
                <div className="analytics-periods">
                    <div className="period-row">
                        <label className="period-label">Период 1:</label>
                        <input type="date" value={period1From} onChange={e => handlePeriod1Change(e.target.value, period1To)} className="period-input" />
            
                        <span className="period-separator">—</span>
                        <input type="date" value={period1To} onChange={e => handlePeriod1Change(period1From, e.target.value)} className="period-input" />
            
                    </div>
                    <div className="period-row">
                        <label className="period-label">Период 2:</label>
                        <input type="date" value={period2From} onChange={e => setPeriod2From(e.target.value)} className="period-input" />
            
                        <span className="period-separator">—</span>
                        <input type="date" value={period2To} onChange={e => setPeriod2To(e.target.value)} className="period-input" />
            
                        <span className="period-hint">(автозаполнение)</span>
                    </div>
                </div>

                <div className="analytics-presets">
                    <span className="presets-label">Быстрый выбор:</span>
                    <button type="button" className="btn btn-sm btn-ghost" onClick={() => applyPreset('week')}>
                        Неделя
                    </button>
                    <button type="button" className="btn btn-sm btn-ghost" onClick={() => applyPreset('month')}>
                        Месяц
                    </button>
                    <button type="button" className="btn btn-sm btn-ghost" onClick={() => applyPreset('quarter')}>
                        Квартал
                    </button>
                    <button type="button" className="btn btn-sm btn-ghost" onClick={swapPeriods} title="Поменять периоды местами">
                        ⇄
                    </button>
                </div>

                <div className="analytics-group-toggle">
                    <button
                        type="button"
                        className={`btn btn-sm ${groupBy === 'sku' ? 'btn-primary' : 'btn-ghost'}`}
                        onClick={() => setGroupBy('sku')}
                    >
                        По артикулам
                    </button>
                    <button
                        type="button"
                        className={`btn btn-sm ${groupBy === 'model' ? 'btn-primary' : 'btn-ghost'}`}
                        onClick={() => setGroupBy('model')}
                    >
                        По моделям
                    </button>
                </div>

                <div className="analytics-actions">
                    <button type="button" className="btn btn-primary" onClick={fetchComparison} disabled={loading || accountIds.length === 0}>
            
                        {loading ? 'Загрузка...' : 'Сравнить'}
                    </button>
                </div>
            </div>

            {/* Filters */}
            {rawComparison && <div className="analytics-filters">
                    <div className="filter-row">
                        <div className="filter-group">
                            <label className="filter-checkbox">
                                <input type="checkbox" checked={hideZeros} onChange={e => setHideZeros(e.target.checked)} />
              
                                Скрыть нулевые
                            </label>
                            <label className="filter-checkbox">
                                <input type="checkbox" checked={showShopColumn} onChange={e => setShowShopColumn(e.target.checked)} />
              
                                Показать магазин
                            </label>
                        </div>

                        <div className="filter-search">
                            <input type="text" placeholder="Поиск по названию, артикулу, SKU..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className="filter-input" />
            
                        </div>

                        <div className="filter-actions">
                            <button type="button" className="btn btn-sm btn-secondary" onClick={exportCSV} disabled={filteredData.length === 0}>
              
                                Скачать CSV
                            </button>
                        </div>
                    </div>

                    {/* SKU Filter */}
                    <div className="sku-filter-section">
                        <div className="sku-filter-input-row">
                            <div className="product-search-wrapper">
                                <input type="text" placeholder="Добавить товар в фильтр..." value={productSearch} onChange={e => setProductSearch(e.target.value)} className="filter-input" />
              
                                {productResults.length > 0 && <div className="product-search-results">
                                        {productResults.map((product, idx) => <div key={idx} className="product-search-result-item" onClick={() => addToFilter(String(product.sku), product.name)}>
                  
                                                <strong>{product.name}</strong>
                                                <br />
                                                <small>Артикул: {product.offer_id}, SKU: {product.sku}</small>
                                            </div>)}
                                    </div>}
                            </div>
                            <button type="button" className="btn btn-sm btn-ghost" onClick={() => setShowPasteModal(true)}>
              
                                Вставить
                            </button>
                            {skuFilter.length > 0 && <button type="button" className="btn btn-sm btn-ghost" onClick={clearFilter}>
              
                                    Очистить фильтр
                                </button>}
                        </div>

                        {skuFilter.length > 0 && <div className="sku-filter-chips">
                                {skuFilter.map((f, idx) => <div key={idx} className="sku-chip">
                                        <span>{f.name || f.sku}</span>
                                        <button type="button" className="sku-chip-remove" onClick={() => removeFromFilter(f.sku)}>
                
                                            ×
                                        </button>
                                    </div>)}
                            </div>}
                    </div>
                </div>}

            {/* Results */}
            {rawComparison && aggregatedStats && <>
                    {/* Aggregated stats */}
                    <div className="analytics-summary">
                        <h3>Общая статистика {skuFilter.length > 0 && <span className="summary-filtered">(отфильтровано)</span>}</h3>
                        <table className="summary-table">
                            <thead>
                                <tr>
                                    <th>Период</th>
                                    <th className="text-right">Заказано на сумму</th>
                                    <th className="text-right">Заказано товаров</th>
                                    <th className="text-right">Средний чек</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr>
                                    <td>{formatDate(rawComparison.period1.from)} — {formatDate(rawComparison.period1.to)}</td>
                                    <td className="text-right">{formatNumber(aggregatedStats.revenue?.period1)}</td>
                                    <td className="text-right">{formatNumber(aggregatedStats.ordered_units?.period1)}</td>
                                    <td className="text-right">{formatNumber(aggregatedStats.average_check?.period1)}</td>
                                </tr>
                                <tr>
                                    <td>{formatDate(rawComparison.period2.from)} — {formatDate(rawComparison.period2.to)}</td>
                                    <td className="text-right">{formatNumber(aggregatedStats.revenue?.period2)}</td>
                                    <td className="text-right">{formatNumber(aggregatedStats.ordered_units?.period2)}</td>
                                    <td className="text-right">{formatNumber(aggregatedStats.average_check?.period2)}</td>
                                </tr>
                                <tr className="summary-change-row">
                                    <td>Изменение</td>
                                    <td className={`text-right ${getChangeClass(aggregatedStats.revenue?.change_percent)}`}>
                                        {formatPercent(aggregatedStats.revenue?.change_percent)}
                                    </td>
                                    <td className={`text-right ${getChangeClass(aggregatedStats.ordered_units?.change_percent)}`}>
                                        {formatPercent(aggregatedStats.ordered_units?.change_percent)}
                                    </td>
                                    <td className={`text-right ${getChangeClass(aggregatedStats.average_check?.change_percent)}`}>
                                        {formatPercent(aggregatedStats.average_check?.change_percent)}
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    </div>

                    {/* Detail table */}
                    <div className="analytics-detail">
                        <h3>{groupBy === 'model' ? `Детализация по моделям (${filteredData.length})` : `Детализация по товарам (${filteredData.length})`}</h3>
                        <div className="analytics-table-wrapper">
                            <table className="analytics-table">
                                <thead>
                                    <tr>
                                        {showShopColumn && <th className="sticky-col col-shop">Магазин</th>}
                                        {groupBy === 'model' ? (
                                            <>
                                                <th className="sticky-col col-name clickable" onClick={() => handleSort('name')}>
                                                    Модель <SortIndicator field="name" />
                                                </th>
                                                <th className="sticky-col col-sku clickable" onClick={() => handleSort('sku_count')}>
                                                    Товаров <SortIndicator field="sku_count" />
                                                </th>
                                            </>
                                        ) : (
                                            <>
                                                <th className="sticky-col col-article clickable" onClick={() => handleSort('offer_id')}>
                                                    Артикул <SortIndicator field="offer_id" />
                                                </th>
                                                <th className="sticky-col col-image">Фото</th>
                                                <th className="sticky-col col-sku clickable" onClick={() => handleSort('sku')}>
                                                    SKU <SortIndicator field="sku" />
                                                </th>
                                                <th className="col-name clickable" onClick={() => handleSort('name')}>
                                                    Название <SortIndicator field="name" />
                                                </th>
                                            </>
                                        )}
                                        <th className="col-metric clickable" onClick={() => handleSort('revenue_p1')}>
                                            Сумма (П1) <SortIndicator field="revenue_p1" />
                                        </th>
                                        <th className="col-metric clickable" onClick={() => handleSort('revenue_p2')}>
                                            Сумма (П2) <SortIndicator field="revenue_p2" />
                                        </th>
                                        <th className="col-delta clickable" onClick={() => handleSort('revenue_change')}>
                                            Δ <SortIndicator field="revenue_change" />
                                        </th>
                                        <th className="col-pct clickable" onClick={() => handleSort('revenue_pct')}>
                                            % <SortIndicator field="revenue_pct" />
                                        </th>
                                        <th className="col-metric clickable" onClick={() => handleSort('ordered_units_p1')}>
                                            Шт (П1) <SortIndicator field="ordered_units_p1" />
                                        </th>
                                        <th className="col-metric clickable" onClick={() => handleSort('ordered_units_p2')}>
                                            Шт (П2) <SortIndicator field="ordered_units_p2" />
                                        </th>
                                        <th className="col-delta clickable" onClick={() => handleSort('ordered_units_change')}>
                                            Δ <SortIndicator field="ordered_units_change" />
                                        </th>
                                        <th className="col-pct clickable" onClick={() => handleSort('ordered_units_pct')}>
                                            % <SortIndicator field="ordered_units_pct" />
                                        </th>
                                        <th className="col-metric clickable" onClick={() => handleSort('avg_check_p1')}>
                                            Чек (П1) <SortIndicator field="avg_check_p1" />
                                        </th>
                                        <th className="col-metric clickable" onClick={() => handleSort('avg_check_p2')}>
                                            Чек (П2) <SortIndicator field="avg_check_p2" />
                                        </th>
                                        <th className="col-delta clickable" onClick={() => handleSort('avg_check_change')}>
                                            Δ <SortIndicator field="avg_check_change" />
                                        </th>
                                        <th className="col-pct clickable" onClick={() => handleSort('avg_check_pct')}>
                                            % <SortIndicator field="avg_check_pct" />
                                        </th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {filteredData.map((item, idx) => {
                const pd = productData[item.sku] || {};
                // Get shop name(s) for this SKU
                const shopName = item.sourceAccountIds && item.sourceAccountIds.length > 1 ? item.sourceAccountIds.map(id => accountNameMap[id] || id).join(', ') : accountNameMap[item.sourceAccountId] || accountName || '-';
                return <tr key={idx}>
                                                {showShopColumn && <td className="sticky-col col-shop">{shopName}</td>}
                                                {groupBy === 'model' ? (
                                                    <>
                                                        <td className="sticky-col col-name" title={item.name}>
                                                            <span className="cell-truncate">{item.name}</span>
                                                        </td>
                                                        <td className="sticky-col col-sku text-right">{item.sku_count || '-'}</td>
                                                    </>
                                                ) : (
                                                    <>
                                                        <td className="sticky-col col-article">{pd.offer_id || '-'}</td>
                                                        <td className="sticky-col col-image">
                                                            {pd.image ? <img src={pd.image} alt="" className="analytics-thumb" onClick={() => openLightbox && openLightbox([pd.image], 0)} onError={e => {
                              e.target.style.display = 'none';
                            }} /> : <div className="analytics-thumb analytics-thumb--empty">-</div>}
                                                        </td>
                                                        <td className="sticky-col col-sku">
                                                            <a href={`https://www.ozon.ru/product/${item.sku}`} target="_blank" rel="noopener noreferrer" className="sku-link">

                                                                {item.sku}
                                                            </a>
                                                        </td>
                                                        <td className="col-name" title={item.name}>
                                                            <span className="cell-truncate">{item.name}</span>
                                                        </td>
                                                    </>
                                                )}
                                                <td className="col-metric text-right">{formatNumber(item.metrics.revenue?.period1)}</td>
                                                <td className="col-metric text-right">{formatNumber(item.metrics.revenue?.period2)}</td>
                                                <td className={`col-delta text-right ${getChangeClass(item.metrics.revenue?.change)}`}>
                                                    {formatNumber(item.metrics.revenue?.change)}
                                                </td>
                                                <td className={`col-pct text-right ${getChangeClass(item.metrics.revenue?.change_percent)}`}>
                                                    {formatPercent(item.metrics.revenue?.change_percent)}
                                                </td>
                                                <td className="col-metric text-right">{formatNumber(item.metrics.ordered_units?.period1)}</td>
                                                <td className="col-metric text-right">{formatNumber(item.metrics.ordered_units?.period2)}</td>
                                                <td className={`col-delta text-right ${getChangeClass(item.metrics.ordered_units?.change)}`}>
                                                    {formatNumber(item.metrics.ordered_units?.change)}
                                                </td>
                                                <td className={`col-pct text-right ${getChangeClass(item.metrics.ordered_units?.change_percent)}`}>
                                                    {formatPercent(item.metrics.ordered_units?.change_percent)}
                                                </td>
                                                <td className="col-metric text-right">{formatNumber(item.metrics.avg_check?.period1)}</td>
                                                <td className="col-metric text-right">{formatNumber(item.metrics.avg_check?.period2)}</td>
                                                <td className={`col-delta text-right ${getChangeClass(item.metrics.avg_check?.change)}`}>
                                                    {formatNumber(item.metrics.avg_check?.change)}
                                                </td>
                                                <td className={`col-pct text-right ${getChangeClass(item.metrics.avg_check?.change_percent)}`}>
                                                    {formatPercent(item.metrics.avg_check?.change_percent)}
                                                </td>
                                            </tr>;
              })}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </>}

            {/* Paste Modal */}
            {showPasteModal && <div className="modal-overlay" onClick={() => setShowPasteModal(false)}>
                    <div className="modal" onClick={e => e.stopPropagation()}>
                        <div className="modal__header">
                            <h3>Вставить артикулы/SKU</h3>
                            <button className="modal__close" onClick={() => setShowPasteModal(false)}>×</button>
                        </div>
                        <div className="modal__body">
                            <p style={{
            marginBottom: '12px',
            color: 'var(--ink-soft)'
          }}>
                                Вставьте артикулы или SKU (каждый с новой строки, через запятую или точку с запятой)
                            </p>
                            <textarea value={pasteText} onChange={e => setPasteText(e.target.value)} onKeyDown={e => {
            if (e.ctrlKey && e.key === 'Enter') {
              handlePasteImport();
            }
          }} placeholder="123456789&#10;987654321&#10;111222333" rows="10" style={{
            width: '100%',
            fontFamily: 'monospace',
            fontSize: '0.9rem'
          }} />
            
                            <p style={{
            marginTop: '8px',
            fontSize: '0.85rem',
            color: 'var(--ink-soft)'
          }}>
                                Ctrl+Enter для добавления
                            </p>
                        </div>
                        <div className="modal__footer">
                            <button className="btn btn-secondary" onClick={() => setShowPasteModal(false)}>
                                Отмена
                            </button>
                            <button className="btn btn-primary" onClick={handlePasteImport}>
                                Добавить в фильтр
                            </button>
                        </div>
                    </div>
                </div>}
        </div>;
}
export default OzonAnalytics;
